import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormField from "components/FormField/FormField";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Modal from "modules/Modal/Modal";
import useAddMaterialNoPlantToProposalProduct from "hooks/proposals/useAddMaterialNoPlantToProposalProduct";
import { ProposalProduct } from "generated/graphql";
import currencyEndAdornment from "constants/currencyEndAdornment";

interface FormData {
  name: string;
  percentage: number;
  rate: number;
  quantityOverride?: number;
}

const schema = z.object({
  name: z.string().min(1, "Material name is required"),
  percentage: z.coerce
    .number({ invalid_type_error: "Percentage is required" })
    .min(0, "Percentage must be 0 or greater")
    .max(100, "Percentage cannot exceed 100"),
  rate: z.coerce
    .number({ invalid_type_error: "Rate is required" })
    .min(0.01, "Rate must be greater than 0"),
  quantityOverride: z.any().optional(),
});

interface AddMaterialNoPlantModalProps {
  open: boolean;
  onClose: () => void;
  product: ProposalProduct;
}

export default function AddMaterialNoPlantModal({
  open,
  onClose,
  product,
}: AddMaterialNoPlantModalProps) {
  const [addMaterialNoPlantToProposalProduct, { loading }] =
    useAddMaterialNoPlantToProposalProduct();

  const materialNeeded =
    product.material?.materialNeededOverride || product.material?.materialNeeded || 0;

  const [calculatedQuantity, setCalculatedQuantity] = useState(Math.round(materialNeeded) || 1);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      percentage: 100,
      rate: 0,
      quantityOverride: undefined,
    },
  });

  const watchedPercentage = watch("percentage");

  useEffect(() => {
    if (watchedPercentage !== undefined && watchedPercentage !== null && materialNeeded) {
      const calculated = Math.round((materialNeeded * watchedPercentage) / 100);
      setCalculatedQuantity(calculated > 0 ? calculated : 1);
    }
  }, [watchedPercentage, materialNeeded]);

  const onSubmit = async (data: FormData) => {
    await addMaterialNoPlantToProposalProduct({
      variables: {
        input: {
          proposalProductId: product.id,
          name: data.name,
          percentage: data.percentage,
          rate: data.rate,
          quantity: calculatedQuantity,
          quantityOverride: data.quantityOverride,
        },
      },
    });
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      styles={{
        width: "500px",
        maxWidth: "90vw",
        height: "auto",
      }}
    >
      <MDBox p={3}>
        <MDTypography variant="h4" mb={3}>
          Add Material
        </MDTypography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                label="Material Name"
                placeholder="Enter material name"
                error={errors.name}
                {...register("name")}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                label="Percentage (%)"
                placeholder="Enter percentage"
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                error={errors.percentage}
                {...register("percentage", { valueAsNumber: true })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CurrencyFormField
                label="Rate"
                placeholder="Enter rate"
                control={control}
                name="rate"
                error={errors.rate}
                {...currencyEndAdornment}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormField
                label="Quantity"
                placeholder={calculatedQuantity.toString()}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                error={errors.quantityOverride}
                {...register("quantityOverride", { valueAsNumber: true })}
              />
            </Grid>
          </Grid>

          <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <MDButton variant="outlined" color="secondary" onClick={handleClose}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info" disabled={loading}>
              {loading ? "Adding..." : "Add Material"}
            </MDButton>
          </MDBox>
        </form>
      </MDBox>
    </Modal>
  );
}
